import { useCallback, useEffect, useRef, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import type { WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService from "services/annotations.service"
import type { TViewerPane } from "services/annotations.service"
import { useDeleteHighlight } from "hooks/api/highlights/highlights.hook"
import { removeHighlightFromCache } from "helpers/quotationCache.helper"
import type { RootEntry } from "../core/shadowPortal"
import type { TPaneBinding } from "../core/viewerPanes"
import type { THighlight } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

export type TSelectedHighlight = { highlight: THighlight; pane: TViewerPane }

export type HighlightSelectContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	getViewerPanes: () => TPaneBinding[]
	layoutEpoch: number
	mode: TViewerMode
	isInitialized: boolean
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	formatError: (error: unknown) => void
	success: (text: string) => void
	// Overlay the floating delete toolbar renders into (a dedicated container, mirroring the batch
	// selection pill's own — see core/overlayContainers.tsx).
	highlightToolbarEntryRef: MutableRefObject<RootEntry | null>
}

// REQ-400 highlight selection, BOTH kinds — figure (rectangle/ellipse, #1323) and freehand (#1324).
// Renamed from useFigureHighlightSelect when #1324 made it serve both; the two differ only in the
// `highlight_type` the DELETE needs, which is read off the highlight's own `shape_type`.
//
// A plain click on a highlight selects it, same as any other entity —
// no separate mode to turn on first. Highlights are the bottom-most layer (#1378), so an
// eref/ecall/furniture/page hit always wins over one; a highlight is only ever reached once the
// orchestrator's own entity hit-test already came back empty (see the mousedown handler in
// useQuotationViewer.tsx), and that handler already gates on the plain Select tool being active and
// no other canvas mode being open — so this hook doesn't need to re-check either.
//
// The bug an earlier "always on" attempt had — picking a highlight while some OTHER entity was
// already selected left that entity live in Apryse's selection too, so e.g. Delete could act on the
// wrong thing (selectAnnotation alone is additive) — is fixed at the source in
// AnnotationService.applySelectedHighlight, which now deselects everything before selecting the
// highlight. That fix holds regardless of how selection is entered, so it isn't re-derived here.
export const useHighlightSelect = (ctx: HighlightSelectContext) => {
	const {
		instanceRef,
		getViewerPanes,
		layoutEpoch,
		mode,
		isInitialized,
		numericQuotationIdRef,
		queryClient,
		formatError,
		success,
		highlightToolbarEntryRef,
	} = ctx

	const [selectedHighlight, setSelectedHighlightState] = useState<TSelectedHighlight | null>(null)
	const selectedHighlightRef = useRef<TSelectedHighlight | null>(null)
	const [isDeletingHighlight, setIsDeletingHighlight] = useState(false)

	const { mutateAsync: deleteHighlightAsync } = useDeleteHighlight()

	// Returns whether it actually cancelled a selection, so the Escape hotkey chain
	// (onCancelCanvasMode) knows to stop here instead of falling through — same contract as the
	// other cancel/close functions in that chain.
	const clearHighlightSelection = useCallback((): boolean => {
		if (!selectedHighlightRef.current) return false
		AnnotationService.deselectHighlight()
		selectedHighlightRef.current = null
		setSelectedHighlightState(null)
		return true
	}, [])

	// Called from the orchestrator's existing entity-click mousedown handler, only after its own
	// entity hit-test came back empty — never races the eref/ecall/furniture/page click path.
	const trySelectHighlightFromMouseEvent = useCallback(
		(event: MouseEvent, pane: TViewerPane): boolean => {
			const hit = AnnotationService.selectHighlightFromMouseEvent(event, pane)
			// #1324 made freehand pickable too — the DELETE's highlight_type now comes from the
			// highlight's own shape_type, so there is nothing left to exclude.
			if (!hit) {
				clearHighlightSelection()
				return false
			}
			const next: TSelectedHighlight = { highlight: hit, pane }
			selectedHighlightRef.current = next
			setSelectedHighlightState(next)
			return true
		},
		[clearHighlightSelection],
	)

	// REQ-400 decision 2: select a just-created highlight programmatically (no mouse event), so the
	// trash toolbar shows immediately after drawing. Handed to useFigureHighlightCreate as its
	// onHighlightCreated callback. Deliberately INDEPENDENT of the picking-mode toggle above — a
	// freshly drawn highlight is selected regardless of whether the mode happens to be on.
	const selectHighlightById = useCallback((highlight: THighlight, pane: TViewerPane) => {
		const hit = AnnotationService.selectHighlightById(highlight, pane)
		if (!hit) return
		const next: TSelectedHighlight = { highlight: hit, pane }
		selectedHighlightRef.current = next
		setSelectedHighlightState(next)
	}, [])

	const deleteSelectedHighlight = useCallback(async () => {
		const current = selectedHighlightRef.current
		const qid = numericQuotationIdRef.current
		if (!current || qid == null || Number.isNaN(qid)) return
		setIsDeletingHighlight(true)
		try {
			// shape_type picks the backend TABLE, not just a label — figures and freehand are separate
			// rows, so a wrong value deletes nothing (see highlights.service).
			await deleteHighlightAsync({
				quotationId: qid,
				highlightId: current.highlight.id,
				shapeType: current.highlight.shape_type,
			})
			AnnotationService.removeHighlight(current.highlight)
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
				removeHighlightFromCache(cached, current.highlight.id),
			)
			selectedHighlightRef.current = null
			setSelectedHighlightState(null)
			success("Highlight deleted")
		} catch (error) {
			formatError(error)
		} finally {
			setIsDeletingHighlight(false)
		}
	}, [numericQuotationIdRef, deleteHighlightAsync, queryClient, formatError, success])

	// Position the floating toolbar above the selected highlight's rendered rect — same math the
	// batch selection pill uses (positionSelectionToolbar in useBatchOperations), sourced from the
	// highlight's own backend page/coords via the existing inverse-of-render lookup instead of
	// re-deriving it from the (inert, metadata-less) annotation.
	const positionHighlightToolbar = useCallback(() => {
		const current = selectedHighlightRef.current
		const entry = highlightToolbarEntryRef.current
		const parent = entry?.container.parentElement
		if (!current || !entry || !parent) return
		const rect = AnnotationService.getClientRectFromBackendRect(
			current.highlight.page_number,
			current.highlight.coordinates,
			current.pane,
		)
		if (!rect) {
			entry.container.style.display = "none"
			return
		}
		const parentRect = parent.getBoundingClientRect()
		entry.container.style.left = `${(rect.left + rect.right) / 2 - parentRect.left}px`
		entry.container.style.top = `${rect.top - parentRect.top}px`
		entry.container.style.display = "block"
	}, [highlightToolbarEntryRef])

	// Keep the toolbar anchored as the document scrolls / zooms / resizes.
	useEffect(() => {
		if (!selectedHighlight) return
		const onMove = () => positionHighlightToolbar()
		const bindings = getViewerPanes().map(({ documentViewer, scrollEl }) => {
			scrollEl.addEventListener("scroll", onMove, { passive: true })
			documentViewer.addEventListener("zoomUpdated", onMove)
			return () => {
				scrollEl.removeEventListener("scroll", onMove)
				documentViewer.removeEventListener("zoomUpdated", onMove)
			}
		})
		window.addEventListener("resize", onMove)
		onMove()
		return () => {
			bindings.forEach((unbind) => unbind())
			window.removeEventListener("resize", onMove)
		}
	}, [selectedHighlight, positionHighlightToolbar, getViewerPanes, layoutEpoch])

	// Drop a selected highlight whenever the tool stops being the plain Select tool —
	// pan/zoom/ruler/shape-draw all take clicks over, and a stale floating toolbar would otherwise be
	// left behind with no tool context that makes sense for it. Primary viewer only: highlights
	// aren't mirrored into the split pane (#1378 scope), so a pick only ever happens there.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { documentViewer, Tools } = instanceRef.current.Core
		const editTool = documentViewer.getTool(Tools.ToolNames.EDIT)
		const onToolModeUpdated = () => {
			if (documentViewer.getToolMode() !== editTool) clearHighlightSelection()
		}
		documentViewer.addEventListener("toolModeUpdated", onToolModeUpdated)
		return () => documentViewer.removeEventListener("toolModeUpdated", onToolModeUpdated)
	}, [mode, isInitialized, instanceRef, clearHighlightSelection, layoutEpoch])

	// Preview mode has no selection affordances — drop the pick, same as the furniture/eref pill.
	useEffect(() => {
		if (mode !== "editor") clearHighlightSelection()
	}, [mode, clearHighlightSelection])

	// Unmount safety net: never leave a highlight's permissions lifted behind us.
	useEffect(() => {
		return () => {
			if (selectedHighlightRef.current) AnnotationService.deselectHighlight()
		}
	}, [])

	return {
		selectedHighlight,
		trySelectHighlightFromMouseEvent,
		selectHighlightById,
		clearHighlightSelection,
		deleteSelectedHighlight,
		isDeletingHighlight,
		positionHighlightToolbar,
	}
}
